import { prepareTransition } from '@archetype-themes/utils/utils'
import { trapFocus, removeTrapFocus, lockScroll, unlockScroll } from '@archetype-themes/utils/a11y'
import { EVENTS } from '@archetype-themes/utils/events'

class HeaderDrawer extends HTMLElement {
  constructor() {
    super()
    this.isOpen = false
    this.abortController = new AbortController()
  }
  
  connectedCallback() {
    if (!this.getAttribute('open') || !this.getAttribute('close')) return
    document.addEventListener(this.getAttribute('open'), this.open.bind(this), { signal: this.abortController.signal })
    document.addEventListener(this.getAttribute('close'), this.close.bind(this), {
      signal: this.abortController.signal
    })
  }

  open(evt) {
    // Salva la posizione di scroll attuale
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop
    
    this.activeElement = evt.target

    // Previeni lo scroll causato dall'evento sizeDrawer
    const originalScrollTo = window.scrollTo
    window.scrollTo = function() {
      // Override temporaneo per impedire qualsiasi scrollTo automatico
    }
    
    this.dispatchEvent(new CustomEvent(EVENTS.sizeDrawer, { bubbles: true }))
    
    // Ripristina la funzione scrollTo originale
    window.scrollTo = originalScrollTo

    // Modifica: prima rendi il drawer attivo
    this.classList.add('is-active')
    
    // Poi ripristina la posizione immediatamente
    window.scrollTo(0, this.scrollPosition)

    // Trap focus (potrebbe causare lo scrolling indesiderato)
    // Ma previeni eventuali scrolling impropri con un altro override temporaneo
    const originalScrollFunction = window.scroll
    window.scroll = function() {}
    
    trapFocus(this)
    
    // Ripristina la funzione scroll originale
    window.scroll = originalScrollFunction
    
    // Ripristina di nuovo la posizione di scroll
    window.scrollTo(0, this.scrollPosition)

    // Blocca lo scrolling ma mantieni la posizione
    lockScroll()
    
    // Prepara la transizione dopo che tutto è impostato
    prepareTransition(this, function() {
      // Il drawer è già attivo, quindi non facciamo nulla qui
    }.bind(this))
    
    // Ripristina la posizione più volte in modo asincrono
    const restorePosition = () => window.scrollTo(0, this.scrollPosition)
    requestAnimationFrame(restorePosition)
    setTimeout(restorePosition, 0)
    setTimeout(restorePosition, 10)
    setTimeout(restorePosition, 50)
    setTimeout(restorePosition, 100)
    
    // Esc key closes the drawer
    window.addEventListener('keyup', this.handleWindowKeyup, { signal: this.abortController.signal })

    this.dispatchEvent(new CustomEvent(EVENTS.headerOverlayRemoveClass, { bubbles: true }))
    this.dispatchEvent(new CustomEvent(EVENTS.headerDrawerOpened, { bubbles: true }))
    this.dispatchEvent(new CustomEvent('drawerOpen', { bubbles: true }))

    this.isOpen = true

    // Clicking out of the drawer closes it
    setTimeout(() => {
      window.addEventListener('click', this.handleWindowClick, { signal: this.abortController.signal })
    }, 0)
  }

  close(evt, noAnimate) {
    // Do not close if click event came from inside drawer
    if (evt && evt.target.closest && evt.target.closest('.site-header__drawer')) {
      return
    }

    if (!this.isOpen) {
      return
    }

    if (noAnimate) {
      this.classList.remove('is-active')
    } else {
      prepareTransition(
        this,
        function () {
          this.classList.remove('is-active')
        }.bind(this)
      )
    }

    this.dispatchEvent(new CustomEvent(EVENTS.headerDrawerClosed, { bubbles: true }))

    window.removeEventListener('keyup', this.handleWindowKeyup)
    window.removeEventListener('click', this.handleWindowClick)

    this.isOpen = false

    // Remove focus trap
    if (this.activeElement) removeTrapFocus(this.activeElement)

    unlockScroll()
    
    // Dopo lo sblocco dello scroll, mantieni la stessa posizione
    if (this.scrollPosition !== undefined) {
      setTimeout(() => {
        window.scrollTo(0, this.scrollPosition)
      }, 0)
    }
  }

  handleWindowKeyup = (evt) => {
    if (evt.keyCode === 27) {
      this.close()
    }
  }

  handleWindowClick = (evt) => {
    this.close(evt)
  }

  disconnectedCallback() {
    this.abortController.abort()
  }
}

customElements.define('header-drawer', HeaderDrawer)
